<template>
    <header>
        <section class="bottom">
            <div class="bottom-title">
                <p>Profit & Loss</p>
            </div>

            <div style="margin-right: 20px;">
                <ejs-dropdownlist :width="100" :dataSource='currencies' :fields="{ text: 'value', value: 'key' }" v-model:value="currency"></ejs-dropdownlist>
            </div>

            <nav>
                <router-link :to="'/studies/' + this.$route.params.id"><i class="fas fa-caret-left"></i> Back</router-link>
                <a href="#" v-on:click="save"><i class="fas fa-file-excel"></i> Export</a>
                <a href="#" v-show="!isBookmarked" v-on:click="createBookmark"><i class="fas fa-bookmark"></i> Bookmark</a>
                <a href="#" v-show="isBookmarked" v-on:click="removeBookmark"><i class="far fa-bookmark"></i> Remove Bookmark</a>
            </nav>
        </section>
    </header>

    <!--
        JG: Settings Notes:
        "allowEditing" - Needs to be true for formulas to calculate https://www.syncfusion.com/forums/109560/formulas-in-excel-spreadsheet-do-not-calculate-until-enable-editing-is-clicked
        "allowCellFormatting" - Needs to be true to allow column, cell formatting through code
        "allowNumberFormatting" - Needs to be true to allow formatting to two decimal places through code
        "cellEdit" - Event that ties into when a user tries to edit a cells. Used to instantly end editing do gget equivalent of allowEditing = false but with formulas.
    -->
    <ejs-spreadsheet id="spreadsheet" v-show="!isFormattingReduced" ref="spreadsheet" v-if="!resetting"
                     :allowEditing="true"
                     :allowCellFormatting="true"
                     :allowChart="false"
                     :allowConditionalFormat="false"
                     :allowDataValidation="false"
                     :allowDelete="true"
                     :allowFiltering="true"
                     :allowFindAndReplace="false"
                     :allowFreezePane="false"
                     :allowHyperlink="false"
                     :allowImage="false"
                     :allowInsert="true"
                     :allowMerge="false"
                     :allowNumberFormatting="true"
                     :allowOpen="false"
                     :allowResizing="true"
                     :allowSave="true"
                     :allowScrolling="true"
                     :allowSorting="true"
                     :allowUndoRedo="false"
                     :showFormulaBar="true"
                     :showRibbon="false"
                     :height="height"
                     :isLazyUpdate="false"
                     :cellEdit="cellEdit"
                     :actionComplete="actionComplete"></ejs-spreadsheet>

    <div v-show="isFormattingReduced" style="width: 100%; text-align: center; color: darkred; font-family: Oswald,sans-serif;">
        <h3>Data for this Profit & Loss report is to large to preview/format. Please click 'Export' to download a copy of this Profit & Loss' data</h3>
    </div>
</template>

<script>
    import api from '../modules/ApiClient';
    import { SpreadsheetComponent } from "@syncfusion/ej2-vue-spreadsheet";
    import { DialogUtility } from '@syncfusion/ej2-popups';
    import Session from "../modules/Session";
    import { ProfitAndLossModel } from '../models/ProfitAndLossModel';
    import { DropDownListComponent } from "@syncfusion/ej2-vue-dropdowns";
    import Bookmark from "../modules/Bookmark";
    import { createElement } from '@syncfusion/ej2-base';
    import { toRaw } from 'vue';
    import { Currency, DialogMessages, ReportNames, Routes } from '../modules/Constants';

    export default {
        name: 'ProfitAndLossComponent',
        components: {
            "ejs-spreadsheet": SpreadsheetComponent,
            'ejs-dropdownlist': DropDownListComponent
        },
        methods: {
            save: function () {

                this.Dialog = DialogUtility.alert({
                    title: `Exporting ${ReportNames.PROFIT_AND_LOSS}`,
                    content: `Exporting  ${ReportNames.PROFIT_AND_LOSS} to Excel. Depending on the size of the selected Business Case this may take awhile. Please Wait`,
                    okButton: { cssClass: 'hidden' },
                    showCloseIcon: false,
                    closeOnEscape: false,
                    animationSettings: { effect: 'Zoom' }
                });

                if (this.isFormattingReduced) {
                    let data = []
                    this.Tabs.forEach(tab => {
                        let sheet = {};
                        sheet.name = tab.TabName;
                        sheet.data = tab.Display;
                        data.push(sheet);
                    });

                    fetch(Routes.EXPORT_UNFORMATTED, {
                        method: "POST",
                        body: JSON.stringify(data),
                        responseType: 'blob'
                    })
                        .then(response => {
                            
                            response.blob().then((data) => {
                                this.Dialog.hide()
                                var anchor = createElement('a', {
                                    attrs: {
                                        download: `${ReportNames.PROFIT_AND_LOSS} - ${this.$route.params.id}.xlsx`
                                    },
                                });
                                var url = URL.createObjectURL(data);
                                anchor.href = url;
                                document.body.appendChild(anchor);
                                anchor.click();
                                URL.revokeObjectURL(url);
                                document.body.removeChild(anchor);
                            })
                        })
                } else {
                    this.Tabs.sort((a, b) => b.TabIndex - a.TabIndex).forEach((tab, index) => {
                        setTimeout(() => { this.$refs.spreadsheet.goTo(`${tab.TabName}!A1`) }, this.tabLoadingTimer * index);
                    });
                    setTimeout(() => { this.$refs.spreadsheet.save({ fileName: `${ReportNames.PROFIT_AND_LOSS} - ${this.$route.params.id}.xlsx`, url: Routes.EXPORT_FORMATTED, saveType: "Xlsx" }) }, (this.Tabs.length + 1) * this.tabLoadingTimer);
                    setTimeout(() => { this.Dialog.hide() }, (this.Tabs.length + 2) * this.tabLoadingTimer);
                }
            },
            setSpreadsheetHeight: function () {
                this.height = document.documentElement.clientHeight - document.getElementById("spreadsheet").offsetTop - 1;
            },
            createBookmark: function () {
                Bookmark.CreateBookmark(this.$route.path, `${ReportNames.PROFIT_AND_LOSS} | ${this.APIData.version.ECRNumber.Value.Fields[0]} | ${this.APIData.version.Name}`, new Date());
                this.isBookmarked = true;
                this.$emit(Bookmark.EventName);
            },
            removeBookmark: function () {
                Bookmark.RemoveBookmark(this.$route.path);
                this.isBookmarked = false;
                this.$emit(Bookmark.EventName);
            },
            cellEdit: function () {
                //JG: Used to cancel and not allow the edit as we dont want users making changes, but do want formulas to run.
                this.$refs.spreadsheet.closeEdit();
            },
            processTabs: function () {
                let data = toRaw(this.APIData);
                data.currency = this.currency;
                this.Tabs.forEach(tab => {
                    tab.process(data)
                })

                //Handle Sheets
                if (this.Tabs.filter(x => x.isFormattingReduced === true).length > 0) {
                    this.isFormattingReduced = true;
                }

                if (this.isFormattingReduced === false) {
                    this.Tabs.sort((a, b) => b.TabIndex - a.TabIndex).forEach(tab => {
                        let data = tab.formatSheet();
                        
                        let sheet = {
                            name: tab.TabName,
                            rows: data.rows,
                            columns: data.columns
                        }

                        this.$refs.spreadsheet.insertSheet([sheet]);
                        this.$refs.spreadsheet.applyFilter();

                    });
                    this.$refs.spreadsheet.refresh();
                }

                this.Dialog.hide();
            }
        },
        data() {
            return {
                exchangeRates: [] , 
                currencies: [],
                currency: Currency.USD,
                height: 800,
                isBookmarked: false,
                tabLoadingTimer: 2500,
                APIData: null,
                Tabs: [new ProfitAndLossModel()],
                Dialog: null,
                isFormattingReduced: false,
                resetting: false
            };
        },
        computed: { },
        mounted: function () {
            document.title = `${ReportNames.TAB_PREFIX} - ${ReportNames.PROFIT_AND_LOSS}`;

            window.addEventListener('resize', this.setSpreadsheetHeight);
            this.setSpreadsheetHeight();
            this.isBookmarked = Bookmark.IsBookmarked(this.$route.path);

            Session.WaitToBeAuthenticated(() => {
                    this.Dialog = DialogUtility.alert({
                        title: `Loading ${ReportNames.PROFIT_AND_LOSS}`,
                        content: `Loading Business Case data from NCAS. ${DialogMessages.PLEASE_WAIT}`,
                        okButton: { cssClass: 'hidden' },
                        showCloseIcon: false,
                        closeOnEscape: false,
                        animationSettings: { effect: 'Zoom' }
                    });

                    Promise.all([
                        api.GetCostStudy(this.$route.params.id, this.$route.params.ver),
                        api.GetCalculations(this.$route.params.id, this.$route.params.ver),
                        api.GetSnapshots(this.$route.params.id, this.$route.params.ver),
                        api.GetAvailableVersions(this.$route.params.id)
                    ]).then(data => {

                        let apiData = {};
                        apiData.study = data[0];
                        apiData.calculations = data[1].Calculations;
                        apiData.snapshots = data[2];
                        apiData.version = data[3].find(v => v.Version.id.toLowerCase() === this.$route.params.ver.toLowerCase()).Version;
                        apiData.currency = this.currency;
                        this.APIData = apiData;


                        apiData.study.exchangeRates.forEach(rate => {
                            rate.Item2.Values.forEach(item => {
                                this.exchangeRates.push({
                                    currency: rate.Item1,
                                    year: new Date(item.Date).getUTCFullYear(),
                                    value: item.Value
                                });
                            });
                        });

                        this.currencies = [...new Set(this.exchangeRates.map(x => x.currency))].map(x => {
                            return { key: x, value: x }
                        });


                        let calculationWorker = new Worker(new URL('../workers/calculations-worker.js', import.meta.url));
                        calculationWorker.onmessage = ({ data }) => {
                            this.APIData.calculations_parsed = toRaw(data);
                            this.processTabs();
                        };
                        calculationWorker.onerror = () => {
                            if (this.Dialog) {
                                this.Dialog.hide();
                            }

                            DialogUtility.alert({
                                title: `Error Loading ${ReportNames.PROFIT_AND_LOSS}`,
                                content: `There was an error while loading this ${ReportNames.PROFIT_AND_LOSS}. ${DialogMessages.ERROR}`,
                                okButton: { text: 'OK', click: function () { this.hide() } },
                                showCloseIcon: false,
                                closeOnEscape: false,
                                animationSettings: { effect: 'Zoom' }
                            });
                        }

                        calculationWorker.postMessage(apiData.calculations);

                    }).catch(() => {
                        this.Dialog.hide();
                        DialogUtility.alert({
                            title: `Error Loading ${ReportNames.PROFIT_AND_LOSS}`,
                            content: `There was an error while loading this ${ReportNames.PROFIT_AND_LOSS}. ${DialogMessages.ERROR}`,
                            okButton: { text: 'OK', click: function () { this.hide() } },
                            showCloseIcon: false,
                            closeOnEscape: false,
                            animationSettings: { effect: 'Zoom' }
                        });
                    });
            });
        },
        unmounted() {
            window.removeEventListener('resize', this.setSpreadsheetHeight);
        },
        watch: {
            currency: function () {
                this.Dialog = DialogUtility.alert({
                    title: 'Running FX Conversion',
                    content: `Running FX Conversion on ${ReportNames.PROFIT_AND_LOSS}. ${DialogMessages.PLEASE_WAIT}`,
                    okButton: { cssClass: 'hidden' },
                    showCloseIcon: false,
                    closeOnEscape: false,
                    animationSettings: { effect: 'Zoom' }
                });

                if (this.isFormattingReduced === false) {
                    this.resetting = true;
                    this.$nextTick(() => {
                        this.resetting = false;
                    })
                }

                setTimeout(() => { this.processTabs() }, 2000)
            }
        }
    }
</script>